import React, { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { XIcon } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useProduct } from '@/hooks/api/useProducts'
import { LoadingCard } from './states/LoadingCard'
import { ErrorCard } from './states/ErrorCard'
import { DesktopOverlay } from '@/components/products/cards/ProductGridCard/DesktopOverlay'
import { ProductOverlay } from '@/components/products/ProductOverlay'
import { Badge } from '@/components/shared/atoms/Badge'
import { ProductDto } from '@wtf/shared'
import { ProductImage as ResponsiveProductImage } from '@/components/products/ProductImage'
import { AnalyticsEvents, EventContext } from '@/utils/analytics'

interface ProductGridCardProps {
    productId: string
    editMode: boolean
    onClick?: () => void
    onRemove?: (id: string) => void
    isDragging?: boolean
    priority?: boolean
    hideOverlayBackground?: boolean
    context?: EventContext // Context for CTR tracking
}

// Internal component interfaces
interface ProductImageProps {
    product: ProductDto
    priority: boolean
}

interface ActionButtonsProps {
    product: ProductDto
    editMode: boolean
    onRemove?: (id: string) => void
}

interface EditModeWrapperProps {
    onClick?: () => void
    className: string
    children: React.ReactNode
}

interface InteractiveModeWrapperProps {
    className: string
    productId: string
    product: ProductDto
    menuOpen: boolean
    setMenuOpen: (open: boolean) => void
    hideOverlayBackground: boolean
    onScrollToTop: () => void
    onMouseLeave: () => void
    onCardClick: () => void
    context?: EventContext
    children: React.ReactNode
}

// Utility functions
const getCardClassName = (isDragging: boolean, editMode: boolean): string => {
    const baseClasses =
        'w-full  h-auto object-cover group relative rounded-2xl cursor-pointer overflow-visible bg-gray-100'

    if (isDragging) {
        return `${baseClasses} opacity-70 scale-105 rotate-2 shadow-2xl z-50 touch-none select-none`
    }

    if (editMode) {
        return `${baseClasses} active:scale-[1.02] transition-transform duration-75`
    }

    return baseClasses
}

// Internal components (not exported)
const ProductImage: React.FC<ProductImageProps> = ({ product, priority }) => {
    const image = product.images?.[0]

    if (!image) {
        return (
            <div className="relative h-full w-full overflow-hidden rounded-2xl bg-gray-200">
                <img
                    src="https://placehold.co/600x400?text=No+Image"
                    alt="No image available"
                    className="h-full min-h-[10.938rem] w-full object-cover object-bottom"
                />
            </div>
        )
    }

    return (
        <div className="relative h-full w-full overflow-hidden rounded-2xl bg-gray-200">
            <ResponsiveProductImage
                image={image}
                alt={product.name ?? product.brand ?? 'Product image'}
                className="h-full w-full"
                imgClassName="h-full w-full object-cover object-bottom"
                loading={priority ? 'eager' : 'lazy'}
                sizes="(max-width: 640px) calc((100vw - 3rem) / 2), (max-width: 1024px) calc((100vw - 2rem) / 2), (max-width: 1280px) calc((100vw - 4rem) / 3), (max-width: 1536px) calc((100vw - 6rem) / 4), 300px"
            />
        </div>
    )
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
    product,
    editMode,
    onRemove,
}) => {
    const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        if (onRemove) {
            onRemove(product.id)
        }
    }

    if (!editMode || !onRemove) {
        return null
    }

    return (
        <button
            onClick={handleRemoveClick}
            className="absolute top-1 right-1 z-10 md:top-2 md:right-2"
        >
            <Badge
                icon={<XIcon width={16} height={16} />}
                label=""
                className="cursor-pointer py-2 backdrop-blur-2xl transition-colors hover:bg-black/50"
            />
        </button>
    )
}

const EditModeWrapper: React.FC<EditModeWrapperProps> = ({
    onClick,
    className,
    children,
}) => (
    <div onClick={onClick} className={className}>
        {children}
    </div>
)

const InteractiveModeWrapper: React.FC<InteractiveModeWrapperProps> = ({
    className,
    productId,
    product,
    menuOpen,
    setMenuOpen,
    hideOverlayBackground,
    onScrollToTop,
    onMouseLeave,
    onCardClick,
    context,
    children,
}) => {
    const cardRef = useRef<HTMLDivElement>(null)
    const [hasBeenVisible, setHasBeenVisible] = useState(false)
    const visibilityStartTime = useRef<number>(0)
    const posthog = usePostHog()

    // Intersection Observer for visibility tracking
    // Note: context is intentionally not in dependencies - visibility should only fire once
    // with the context value at the time the card becomes visible, not retrigger on context changes
    useEffect(() => {
        if (!cardRef.current || hasBeenVisible) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasBeenVisible) {
                        // Card became visible - capture context value at this moment
                        const capturedContext = context
                        visibilityStartTime.current = Date.now()
                        setHasBeenVisible(true)

                        // Track visibility after 1 second
                        const timeout = setTimeout(() => {
                            if (entry.isIntersecting) {
                                const timeVisible = Math.round(
                                    (Date.now() - visibilityStartTime.current) / 1000
                                )
                                posthog?.capture(AnalyticsEvents.PRODUCT_CARD_VISIBLE, {
                                    product_id: product.id,
                                    product_name: product.name,
                                    brand: product.brand,
                                    price: product.price,
                                    currency: product.currency,
                                    time_visible_seconds: timeVisible,
                                    ...(capturedContext && { context: capturedContext }),
                                })
                            }
                        }, 1000)

                        return () => clearTimeout(timeout)
                    }
                })
            },
            {
                threshold: 0.5, // Track when 50% of card is visible
                rootMargin: '0px',
            }
        )

        observer.observe(cardRef.current)

        return () => {
            observer.disconnect()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBeenVisible, posthog, product])

    const handleClick = () => {
        onCardClick()
        onScrollToTop()
    }

    return (
        <motion.div ref={cardRef} className={className} onMouseLeave={onMouseLeave}>
            {/* Mobile: Link wrapper for navigation */}
            <Link
                onClick={handleClick}
                to="/products/$id"
                resetScroll
                params={{ id: productId }}
                className="block h-full w-full md:pointer-events-none"
                tabIndex={0}
            >
                {children}
            </Link>
            {/* Desktop: Hover overlay with collection controls */}
            <div className="hidden md:block">
                <DesktopOverlay
                    product={product}
                    productId={productId}
                    onMouseLeave={onMouseLeave}
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    hideOverlayBackground={hideOverlayBackground}
                />
            </div>
        </motion.div>
    )
}

export const ProductGridCard: React.FC<ProductGridCardProps> = ({
    productId,
    editMode,
    onClick,
    onRemove,
    isDragging = false,
    priority = false,
    hideOverlayBackground = false,
    context,
}) => {
    const { data: product, isLoading, error } = useProduct(productId)
    const [menuOpen, setMenuOpen] = React.useState(false)
    const posthog = usePostHog()

    // Loading and error states
    if (isLoading) {
        return <LoadingCard />
    }

    if (error || !product) {
        return <ErrorCard />
    }

    // Handlers
    const handleScrollToTop = () => {
        try {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } catch {
            window.scrollTo(0, 0)
        }
    }

    const handleMouseLeave = () => {
        // Don't automatically close dropdown on mouse leave
        // Let click-outside handler in DesktopOverlay manage dropdown closure
        // The overlay visibility is handled by CSS hover states
    }

    const handleCardClick = () => {
        posthog?.capture(AnalyticsEvents.PRODUCT_CARD_CLICKED, {
            product_id: product.id,
            product_name: product.name,
            brand: product.brand,
            price: product.price,
            currency: product.currency,
            ...(context && { context }),
        })
        // Call parent onClick callback if provided
        onClick?.()
    }

    // Compute card styling based on state
    const cardClassName = getCardClassName(isDragging, editMode)

    // Shared card content
    const cardContent = (
        <>
            <ProductImage product={product} priority={priority} />
            {/* Mobile: left variant (price top-left, brand bottom-left) */}
            <div className="md:hidden">
                <ProductOverlay product={product} variant="left" />
            </div>
            {/* Desktop: bottom variant (brand bottom-left, price bottom-right) */}
            <div className="hidden group-hover:hidden md:block">
                <ProductOverlay product={product} variant="bottom" />
            </div>
            <ActionButtons
                product={product}
                editMode={editMode}
                onRemove={onRemove}
            />
        </>
    )

    // Edit mode: simple div wrapper
    if (editMode) {
        return (
            <EditModeWrapper onClick={onClick} className={cardClassName}>
                {cardContent}
            </EditModeWrapper>
        )
    }

    // Non-edit mode: Mobile Link wrapper + Desktop motion.div with hover overlay
    return (
        <InteractiveModeWrapper
            className={cardClassName}
            productId={productId}
            product={product}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            hideOverlayBackground={hideOverlayBackground}
            onScrollToTop={handleScrollToTop}
            onMouseLeave={handleMouseLeave}
            onCardClick={handleCardClick}
            context={context}
        >
            {cardContent}
        </InteractiveModeWrapper>
    )
}
